import type { SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import { SlideDeckMaterialType } from '@breakoutlearning/firebase-repository/types'

// Podcasts carry two files: materialLink is the audio and imageUrl is the
// cover. Every other material's image is materialLink, so callers get a URL
// rather than a material and cannot reach for the wrong field.
type CardThumbnail = { src: string; alt: string }

// A material is allocated before its file is uploaded, so a record can exist
// with an empty URL, and nothing stops a deck holding several materials of one
// kind. A tier is only satisfied by a material that actually has a URL.
const firstWithUrl = (
  materials: SlideDeckMaterial[],
  matches: (material: SlideDeckMaterial) => boolean,
  urlOf: (material: SlideDeckMaterial) => string | undefined
): CardThumbnail | undefined => {
  for (const material of materials) {
    const src = matches(material) ? urlOf(material) : undefined
    if (src) return { src, alt: material.data.materialName }
  }
  return undefined
}

export function selectSquareThumbnail(
  materials: SlideDeckMaterial[]
): CardThumbnail | undefined {
  return (
    firstWithUrl(
      materials,
      (m) => m.data.materialType === SlideDeckMaterialType.featuredSquare,
      (m) => m.data.materialLink
    ) ??
    firstWithUrl(
      materials,
      (m) => m.isPodcast,
      (m) => m.data.imageUrl
    ) ??
    // Last resort: the hero is 16:9 and gets centre-cropped to fit the square.
    firstWithUrl(
      materials,
      (m) => m.data.materialType === SlideDeckMaterialType.featuredLarge,
      (m) => m.data.materialLink
    )
  )
}

// The 16:9 banner shows the trailer's poster frame when the trailer is
// playable, which is why the trailer thumbnail is preferred here and never in
// the square slot.
export function selectBannerThumbnail(
  materials: SlideDeckMaterial[],
  trailer: SlideDeckMaterial | undefined
): CardThumbnail | undefined {
  if (trailer) {
    const trailerThumbnail = materials.find(
      (m) =>
        m.data.materialType === SlideDeckMaterialType.img &&
        m.data.materialName === 'Trailer Thumbnail'
    )
    if (trailerThumbnail)
      return {
        src: trailerThumbnail.data.materialLink,
        alt: trailerThumbnail.data.materialName,
      }
  }

  const hero = materials.find(
    (m) => m.data.materialType === SlideDeckMaterialType.featuredLarge
  )
  return hero
    ? { src: hero.data.materialLink, alt: hero.data.materialName }
    : undefined
}
